<template>
  <div class="container-fluid">
    <div class="page-title">
      <div class="row">
        <div class="col-md-6">
          <h3>Viajes publicados</h3>
        </div>
        <div class="col-md-6 text-end">
          <report-export-buttons
            title="Reporte de viajes publicados"
            subtitle="Resultados de mapa de viajes publicados"
            file-name="reporte-viajes-publicados"
            :columns="reportColumns"
            :rows="reportRows"
            :disabled="loading"
          />
        </div>
      </div>
    </div>

    <div class="card mb-3">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-3">
            <label class="form-label">Origen</label>
            <input v-model.trim="filters.origen" type="text" class="form-control" placeholder="Buscar origen" />
          </div>
          <div class="col-md-3">
            <label class="form-label">Destino</label>
            <input v-model.trim="filters.destino" type="text" class="form-control" placeholder="Buscar destino" />
          </div>
          <div class="col-md-2">
            <label class="form-label">Fecha</label>
            <input v-model="filters.fecha" type="date" class="form-control" :disabled="filters.soloFechaActual" />
          </div>
          <div class="col-md-2">
            <label class="form-label">Punto de busqueda</label>
            <select v-model="filters.tipoUbicacion" class="form-select">
              <option value="AMBOS">Origen y destino</option>
              <option value="ORIGEN">Solo origen</option>
              <option value="DESTINO">Solo destino</option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label">Radio (km)</label>
            <select v-model="selectedRadiusPreset" class="form-select" @change="onRadiusPresetChange">
              <option value="1">1 km</option>
              <option value="5">5 km</option>
              <option value="10">10 km</option>
              <option value="15">15 km</option>
              <option value="custom">Personalizado</option>
            </select>
          </div>
          <div class="col-md-2" v-if="selectedRadiusPreset === 'custom'">
            <label class="form-label">Radio personalizado (km)</label>
            <input
              v-model.number="filters.radioKm"
              type="number"
              min="0.1"
              step="0.1"
              class="form-control"
              placeholder="Ej. 7.5"
            />
          </div>
          <div class="col-md-2">
            <div class="form-check mt-4">
              <input id="soloHoy" v-model="filters.soloFechaActual" class="form-check-input" type="checkbox" />
              <label class="form-check-label" for="soloHoy">Solo viajes de hoy</label>
            </div>
          </div>
          <div class="col-md-3 d-flex gap-2">
            <button class="btn btn-outline-primary w-100" :disabled="gettingLocation" @click="usarUbicacionActual">
              {{ gettingLocation ? 'Obteniendo ubicacion...' : 'Mi ubicacion' }}
            </button>
          </div>
          
          <div class="col-md-3 text-end">
            <button class="btn btn-primary me-2" :disabled="loading || !mapReady" @click="search">Buscar</button>
            <button class="btn btn-outline-danger" :disabled="loading" @click="clearFilters">Limpiar</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="errorMsg" class="alert alert-danger" role="alert">{{ errorMsg }}</div>
    <div v-else-if="fallbackSearchCenter" class="alert alert-info" role="alert">
      La busqueda por origen/destino se centro en el punto medio del estado:
      <strong>{{ fallbackSearchCenter.label }}</strong>.
    </div>

    <div class="row">
      <div class="col-xl-4 mb-3">
        <div class="card h-100">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="mb-0">Resumen de viajes</h6>
              <span class="summary-counter">{{ trips.length }}</span>
            </div>
            <div v-if="trips.length === 0" class="text-muted small py-2">
              No hay viajes para los filtros seleccionados.
            </div>
            <div v-else class="trip-summary-list">
              <button
                v-for="trip in trips"
                :key="trip.id"
                type="button"
                class="trip-summary-item"
                :class="{ active: selectedTripId === trip.id }"
                @click="focusTripOnMap(trip)"
              >
                <div class="trip-summary-head">
                  <div class="trip-summary-user">{{ trip.travelerName || 'Sin viajero' }}</div>
                  <div class="trip-summary-date">{{ formatDate(trip.tripDate) }} {{ formatTime(trip.tripTime) }}</div>
                </div>
                <div class="trip-summary-route">
                  <div class="trip-route-row">
                    <span class="trip-dot origin"></span>
                    <span class="trip-route-label">Origen</span>
                    <span class="trip-route-value">{{ trip.originAddress || '-' }}</span>
                  </div>
                  <div class="trip-route-row">
                    <span class="trip-dot destination"></span>
                    <span class="trip-route-label">Destino</span>
                    <span class="trip-route-value">{{ trip.destinationAddress || '-' }}</span>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl-8 mb-3">
        <div class="card h-100">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="fw-semibold">Mapa de viajes</span>
              <span class="small text-muted">{{ trips.length }} viajes encontrados</span>
            </div>
            <div v-if="loading" class="text-center py-3">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Cargando...</span>
              </div>
            </div>
            <div ref="mapContainer" class="published-map"></div>
            
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { tripsApi } from '@/api/trips';
import origenMarkerIcon from '@/assets/origen.png';
import destinoMarkerIcon from '@/assets/destino.png';
import ReportExportButtons from '@/components/reports/ReportExportButtons.vue';

export default {
  name: 'ViajesPublicados',
  components: { ReportExportButtons },
  data() {
    return {
      map: null,
      mapReady: false,
      loading: false,
      gettingLocation: false,
      errorMsg: '',
      trips: [],
      markers: [],
      selectedTripId: null,
      currentLocationMarker: null,
      currentLocationCircleId: 'current-location-radius',
      selectedRadiusPreset: '5',
      radioEnabledByLocation: false,
      markerIconUrls: {
        origen: origenMarkerIcon,
        destino: destinoMarkerIcon,
      },
      filters: {
        origen: '',
        destino: '',
        fecha: '',
        soloFechaActual: true,
        tipoUbicacion: 'AMBOS',
        radioKm: 5,
        latitude: null,
        longitude: null,
      },
      fallbackSearchCenter: null,
    };
  },
  computed: {
    hasCurrentLocation() {
      return Number.isFinite(this.filters.latitude) && Number.isFinite(this.filters.longitude);
    },
    reportColumns() {
      return [
        { key: 'travelerName', label: 'Viajero' },
        { key: 'tripDate', label: 'Fecha' },
        { key: 'tripTime', label: 'Hora' },
        { key: 'originAddress', label: 'Origen' },
        { key: 'destinationAddress', label: 'Destino' },
        { key: 'status', label: 'Estado' },
      ];
    },
    reportRows() {
      return this.trips.map((trip) => ({
        travelerName: trip.travelerName || '-',
        tripDate: this.formatDate(trip.tripDate),
        tripTime: this.formatTime(trip.tripTime),
        originAddress: trip.originAddress || '-',
        destinationAddress: trip.destinationAddress || '-',
        status: trip.status || 'ACTIVO',
      }));
    },
  },
  watch: {
    'filters.radioKm'() {
      this.updateCurrentRadiusCircle();
    },
  },
  async mounted() {
    this.initMap();
    await this.fetchTripsForMap();
  },
  beforeUnmount() {
    this.clearTripMarkers();
    if (this.currentLocationMarker) {
      this.currentLocationMarker.remove();
      this.currentLocationMarker = null;
    }
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
  methods: {
    initMap() {
      const mapboxgl = window.mapboxgl;
      const mapToken = process.env.VUE_APP_MAPBOX_TOKEN || '';
      if (!mapboxgl || !mapToken) {
        this.errorMsg = 'No se pudo inicializar el mapa. Verifica VUE_APP_MAPBOX_TOKEN.';
        return;
      }
      mapboxgl.accessToken = mapToken;
      this.map = new mapboxgl.Map({
        container: this.$refs.mapContainer,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [-99.6557, 19.2913],
        zoom: 11,
      });
      this.map.on('load', () => {
        this.mapReady = true;
        this.renderTripMarkers();
        this.updateCurrentRadiusCircle();
        this.fitToCurrentLocation();
      });
    },
    buildCircleGeoJSON(centerLng, centerLat, radiusKm) {
      const points = 64;
      const coords = [];
      const earthRadiusKm = 6371;
      const latRad = (centerLat * Math.PI) / 180;
      const deltaLat = (radiusKm / earthRadiusKm) * (180 / Math.PI);
      const deltaLng = deltaLat / Math.cos(latRad || 1);
      for (let i = 0; i <= points; i += 1) {
        const angle = (i / points) * 2 * Math.PI;
        const lng = centerLng + deltaLng * Math.cos(angle);
        const lat = centerLat + deltaLat * Math.sin(angle);
        coords.push([lng, lat]);
      }
      return {
        type: 'FeatureCollection',
        features: [{ type: 'Feature', geometry: { type: 'Polygon', coordinates: [coords] } }],
      };
    },
    updateCurrentRadiusCircle() {
      if (!this.mapReady || !this.hasCurrentLocation || !this.radioEnabledByLocation) return;
      const source = this.map.getSource(this.currentLocationCircleId);
      const data = this.buildCircleGeoJSON(
        this.filters.longitude,
        this.filters.latitude,
        Number(this.filters.radioKm || 5),
      );
      if (source) {
        source.setData(data);
        return;
      }
      this.map.addSource(this.currentLocationCircleId, { type: 'geojson', data });
      this.map.addLayer({
        id: `${this.currentLocationCircleId}-fill`,
        type: 'fill',
        source: this.currentLocationCircleId,
        paint: { 'fill-color': '#0d6efd', 'fill-opacity': 0.08 },
      });
      this.map.addLayer({
        id: `${this.currentLocationCircleId}-stroke`,
        type: 'line',
        source: this.currentLocationCircleId,
        paint: { 'line-color': '#0d6efd', 'line-width': 2, 'line-opacity': 0.6 },
      });
    },
    removeCurrentRadiusCircle() {
      if (!this.mapReady || !this.map) return;
      const fillId = `${this.currentLocationCircleId}-fill`;
      const strokeId = `${this.currentLocationCircleId}-stroke`;
      if (this.map.getLayer(fillId)) this.map.removeLayer(fillId);
      if (this.map.getLayer(strokeId)) this.map.removeLayer(strokeId);
      if (this.map.getSource(this.currentLocationCircleId)) this.map.removeSource(this.currentLocationCircleId);
    },
    createMarkerElement(kind) {
      const el = document.createElement('div');
      el.className = `trip-pin-image-wrapper ${kind === 'origen' ? 'trip-pin-origin' : 'trip-pin-destination'}`;
      const img = document.createElement('img');
      img.className = 'trip-pin-image';
      img.src = kind === 'origen' ? this.markerIconUrls.origen : this.markerIconUrls.destino;
      img.alt = kind === 'origen' ? 'Origen' : 'Destino';
      img.onload = () => {
        el.classList.remove('trip-pin-fallback');
      };
      img.onerror = () => {
        el.classList.add('trip-pin-fallback');
      };
      el.appendChild(img);
      return el;
    },
    clearTripMarkers() {
      this.markers.forEach((m) => m.instance.remove());
      this.markers = [];
    },
    renderTripMarkers() {
      if (!this.mapReady) return;
      this.clearTripMarkers();
      const mapboxgl = window.mapboxgl;
      const tripsToRender = this.selectedTripId
        ? this.trips.filter((trip) => trip.id === this.selectedTripId)
        : this.trips;
      tripsToRender.forEach((trip) => {
        if (Number.isFinite(trip.originLongitude) && Number.isFinite(trip.originLatitude)) {
          const originPopup = new mapboxgl.Popup({ offset: 20 }).setHTML(
            `<strong>Origen</strong><br>${trip.originAddress || '-'}<br><small>${trip.travelerName || ''}</small>`,
          );
          const originMarker = new mapboxgl.Marker({ element: this.createMarkerElement('origen') })
            .setLngLat([trip.originLongitude, trip.originLatitude])
            .setPopup(originPopup)
            .addTo(this.map);
          this.markers.push({
            tripId: trip.id,
            kind: 'origen',
            instance: originMarker,
            popup: originPopup,
            lng: trip.originLongitude,
            lat: trip.originLatitude,
          });
        }
        if (Number.isFinite(trip.destinationLongitude) && Number.isFinite(trip.destinationLatitude)) {
          const destinationPopup = new mapboxgl.Popup({ offset: 20 }).setHTML(
            `<strong>Destino</strong><br>${trip.destinationAddress || '-'}<br><small>${trip.travelerName || ''}</small>`,
          );
          const destinationMarker = new mapboxgl.Marker({ element: this.createMarkerElement('destino') })
            .setLngLat([trip.destinationLongitude, trip.destinationLatitude])
            .setPopup(destinationPopup)
            .addTo(this.map);
          this.markers.push({
            tripId: trip.id,
            kind: 'destino',
            instance: destinationMarker,
            popup: destinationPopup,
            lng: trip.destinationLongitude,
            lat: trip.destinationLatitude,
          });
        }
      });
    },
    focusTripOnMap(trip) {
      if (!this.mapReady || !trip?.id) return;
      if (this.selectedTripId === trip.id) {
        this.selectedTripId = null;
        this.renderTripMarkers();
        return;
      }
      this.selectedTripId = trip.id;
      this.renderTripMarkers();
      const tripMarkers = this.markers.filter((m) => m.tripId === trip.id);
      if (!tripMarkers.length) return;
      const coords = tripMarkers.map((m) => [m.lng, m.lat]);
      if (coords.length === 1) {
        this.map.flyTo({ center: coords[0], zoom: 14 });
      } else {
        let minLng = coords[0][0];
        let maxLng = coords[0][0];
        let minLat = coords[0][1];
        let maxLat = coords[0][1];
        coords.forEach((c) => {
          if (c[0] < minLng) minLng = c[0];
          if (c[0] > maxLng) maxLng = c[0];
          if (c[1] < minLat) minLat = c[1];
          if (c[1] > maxLat) maxLat = c[1];
        });
        this.map.fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat],
          ],
          { padding: 80, duration: 500 },
        );
      }
      const originMarker = tripMarkers.find((m) => m.kind === 'origen') || tripMarkers[0];
      originMarker?.instance?.togglePopup();
    },
    fitToCurrentLocation() {
      if (!this.mapReady || !this.hasCurrentLocation) return;
      this.map.flyTo({
        center: [this.filters.longitude, this.filters.latitude],
        zoom: 12,
      });
    },
    async usarUbicacionActual(fetchAfter = false) {
      if (!navigator.geolocation) {
        this.errorMsg = 'El navegador no soporta geolocalizacion.';
        return;
      }
      this.gettingLocation = true;
      this.errorMsg = '';
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
          });
        });
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        this.radioEnabledByLocation = true;
        this.filters.latitude = lat;
        this.filters.longitude = lng;
        this.fitToCurrentLocation();
        const mapboxgl = window.mapboxgl;
        if (this.currentLocationMarker) this.currentLocationMarker.remove();
        this.currentLocationMarker = new mapboxgl.Marker({ element: this.createCurrentLocationElement() })
          .setLngLat([lng, lat])
          .setPopup(new mapboxgl.Popup({ offset: 16 }).setText('Ubicacion actual'))
          .addTo(this.map);
        this.updateCurrentRadiusCircle();
        if (fetchAfter) {
          await this.fetchTripsForMap();
        }
      } catch {
        this.errorMsg = 'No fue posible obtener la ubicacion actual.';
      } finally {
        this.gettingLocation = false;
      }
    },
    createCurrentLocationElement() {
      const el = document.createElement('div');
      el.className = 'trip-pin-current';
      return el;
    },
    formatDate(d) {
      if (!d) return '-';
      const date = new Date(d);
      return date.toLocaleDateString('es-MX');
    },
    formatTime(d) {
      if (!d) return '-';
      const date = new Date(d);
      return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    },
    buildParams(customCenter = null) {
      const params = {
        tipoUbicacion: this.filters.tipoUbicacion,
        soloFechaActual: this.filters.soloFechaActual,
      };
      if (this.filters.origen) params.origen = this.filters.origen;
      if (this.filters.destino) params.destino = this.filters.destino;
      if (!this.filters.soloFechaActual && this.filters.fecha) params.fecha = this.filters.fecha;

      const hasPlaceFilters = !!(this.filters.origen || '').trim() || !!(this.filters.destino || '').trim();
      const canUseRadiusWithCurrentLocation = this.radioEnabledByLocation && this.hasCurrentLocation && !hasPlaceFilters;
      const canUseRadiusWithCustomCenter =
        customCenter && Number.isFinite(customCenter.latitude) && Number.isFinite(customCenter.longitude);

      if (canUseRadiusWithCurrentLocation || canUseRadiusWithCustomCenter) {
        params.radioKm = Number(this.filters.radioKm || 5);
      }

      if (customCenter && Number.isFinite(customCenter.latitude) && Number.isFinite(customCenter.longitude)) {
        params.latitude = customCenter.latitude;
        params.longitude = customCenter.longitude;
      } else if (canUseRadiusWithCurrentLocation) {
        params.latitude = this.filters.latitude;
        params.longitude = this.filters.longitude;
      }
      return params;
    },
    async fetchTripsForMap(customCenter = null) {
      this.loading = true;
      this.errorMsg = '';
      try {
        const { data } = await tripsApi.getPublishedMap(this.buildParams(customCenter));
        this.trips = data?.items || [];
        if (this.selectedTripId && !this.trips.some((trip) => trip.id === this.selectedTripId)) {
          this.selectedTripId = null;
        }
        this.renderTripMarkers();
        if (!customCenter) this.updateCurrentRadiusCircle();
        return this.trips.length;
      } catch (e) {
        this.errorMsg = e.response?.data?.message || 'Error al consultar viajes publicados en mapa.';
        return 0;
      } finally {
        this.loading = false;
      }
    },
    async resolveStateCenterByQuery(query) {
      if (!query) return null;
      const token = process.env.VUE_APP_MAPBOX_TOKEN || '';
      if (!token) return null;
      try {
        const params = new URLSearchParams({
          access_token: token,
          language: 'es',
          country: 'mx',
          limit: '1',
          types: 'region',
        });
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?${params.toString()}`;
        const resp = await fetch(url);
        if (!resp.ok) return null;
        const data = await resp.json();
        const feature = data?.features?.[0];
        const center = feature?.center;
        if (!Array.isArray(center) || center.length < 2) return null;
        const [longitude, latitude] = center;
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
        return {
          latitude,
          longitude,
          label: feature?.text || feature?.place_name || query,
        };
      } catch {
        return null;
      }
    },
    async search() {
      this.fallbackSearchCenter = null;
      const placeQuery = (this.filters.destino || this.filters.origen || '').trim();
      const shouldUseStateCenter = !!placeQuery;
      if (shouldUseStateCenter) {
        const stateCenter = await this.resolveStateCenterByQuery(placeQuery);
        if (stateCenter) {
          this.fallbackSearchCenter = stateCenter;
          await this.fetchTripsForMap(stateCenter);
          if (this.mapReady) {
            this.map.flyTo({
              center: [stateCenter.longitude, stateCenter.latitude],
              zoom: 8.5,
            });
          }
          return;
        }
      }
      await this.fetchTripsForMap();
      if (this.radioEnabledByLocation && this.hasCurrentLocation && this.mapReady) {
        this.map.flyTo({
          center: [this.filters.longitude, this.filters.latitude],
          zoom: 11.5,
        });
      }
    },
    async clearFilters() {
      this.fallbackSearchCenter = null;
      this.selectedTripId = null;
      this.filters.origen = '';
      this.filters.destino = '';
      this.filters.fecha = '';
      this.filters.soloFechaActual = true;
      this.filters.tipoUbicacion = 'AMBOS';
      this.selectedRadiusPreset = '5';
      this.filters.radioKm = 5;
      this.radioEnabledByLocation = false;
      this.removeCurrentRadiusCircle();
      await this.fetchTripsForMap();
    },
    onRadiusPresetChange() {
      if (this.selectedRadiusPreset !== 'custom') {
        this.filters.radioKm = Number(this.selectedRadiusPreset);
      }
      this.updateCurrentRadiusCircle();
    },
  },
};
</script>

<style scoped>
.published-map {
  width: 100%;
  height: 520px;
  border: 1px solid #dde3f2;
  border-radius: 12px;
}
.trip-summary-list {
  max-height: 520px;
  overflow: auto;
  display: grid;
  gap: 12px;
}
.trip-summary-item {
  border: 1px solid #dfe6f3;
  border-radius: 14px;
  padding: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  box-shadow: 0 3px 10px rgba(22, 44, 88, 0.05);
  text-align: left;
  transition: all 0.2s ease;
}
.trip-summary-item:hover {
  border-color: #b8cdf7;
  box-shadow: 0 8px 18px rgba(13, 61, 145, 0.12);
  transform: translateY(-1px);
}
.trip-summary-item.active {
  border-color: #4b82f1;
  box-shadow: 0 8px 18px rgba(29, 87, 196, 0.18);
  background: linear-gradient(180deg, #f7fbff 0%, #edf4ff 100%);
}
.trip-summary-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}
.trip-summary-user {
  font-size: 1.08rem;
  font-weight: 700;
  color: #12284f;
}
.trip-summary-date {
  font-size: 0.82rem;
  color: #5c6f8f;
}
.trip-summary-route {
  display: grid;
  gap: 7px;
}
.trip-route-row {
  display: grid;
  grid-template-columns: auto auto 1fr;
  align-items: start;
  gap: 8px;
}
.trip-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-top: 5px;
}
.trip-dot.origin {
  background: #1f9d55;
}
.trip-dot.destination {
  background: #e11d48;
}
.trip-route-label {
  font-weight: 700;
  color: #2d3f5f;
  min-width: 54px;
}
.trip-route-value {
  color: #3d4d68;
}
.summary-counter {
  min-width: 26px;
  height: 26px;
  border-radius: 999px;
  background: #ffcc00;
  color: #283247;
  font-weight: 700;
  font-size: 0.85rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
}
.legend-origin {
  background: #1f9d55;
}
.legend-destination {
  background: #e11d48;
}
.legend-current {
  background: #2563eb;
}
</style>

<style>
.trip-pin {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.2);
}
.trip-pin-origin {
  background: #1f9d55;
}
.trip-pin-destination {
  background: #e11d48;
}
.trip-pin-current {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 3px solid #fff;
  background: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.3);
}
.trip-pin-image-wrapper {
  width: 52px;
  height: 62px;
  transform: translateY(-50%);
  cursor: pointer;
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  overflow: visible;
}
.trip-pin-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  background: transparent !important;
}
.trip-pin-image-wrapper.trip-pin-fallback {
  width: 52px;
  height: 62px;
  background: transparent !important;
}
.trip-pin-image-wrapper.trip-pin-fallback .trip-pin-image {
  opacity: 0.65;
}
</style>
